(() => {
  if (customElements.get('collection-card')) return;

  const COLOR_PARAM_RE = /color|colour/i;

  function normalizeColor(value) {
    if (!value) return '';
    try {
      return decodeURIComponent(String(value)).trim().toLowerCase();
    } catch {
      return String(value).trim().toLowerCase();
    }
  }

  function getActiveFilterColorsFromUrl() {
    const colors = [];
    const params = new URLSearchParams(window.location.search);
    params.forEach((value, key) => {
      if (COLOR_PARAM_RE.test(key)) colors.push(normalizeColor(value));
    });
    return colors;
  }

  function urlHasColorFilterParams() {
    return [...new URLSearchParams(window.location.search).keys()].some((key) => COLOR_PARAM_RE.test(key));
  }

  function normalizeHex(value) {
    if (!value) return '';
    return String(value).trim().toLowerCase();
  }

  function normalizeImageKey(url) {
    if (!url) return '';
    try {
      return new URL(url, window.location.origin).pathname;
    } catch {
      return String(url).split('?')[0];
    }
  }

  function getActiveFilterSwatches() {
    const colors = [];
    const images = [];
    const form = document.querySelector('[data-filters-form]');

    if (form) {
      form.querySelectorAll('[data-filter-input][data-filter-color]:checked').forEach((input) => {
        const labelColor = normalizeColor(input.dataset.filterColor);
        if (labelColor) colors.push(labelColor);
        const swatchColor = normalizeHex(input.dataset.filterSwatchColor);
        if (swatchColor) colors.push(swatchColor);
        const swatchImage = normalizeImageKey(input.dataset.filterSwatchImage);
        if (swatchImage) images.push(swatchImage);
      });
    }

    if (!colors.length && !images.length && urlHasColorFilterParams()) {
      getActiveFilterColorsFromUrl().forEach((color) => colors.push(color));
    }

    return {
      colors: [...new Set(colors.filter(Boolean))],
      images: [...new Set(images.filter(Boolean))],
    };
  }

  function swatchMatchesFilters(swatch, filterColors, filterImages) {
    const swatchColor = normalizeColor(swatch.dataset.swatchColor);
    const swatchLabel = normalizeColor(swatch.getAttribute('aria-label'));
    const swatchHex = normalizeHex(swatch.dataset.swatchHex);
    const swatchImageKey = normalizeImageKey(swatch.dataset.swatchImageKey);

    for (const filterColor of filterColors) {
      if (swatchColor === filterColor || swatchLabel === filterColor) return true;
      if (swatchHex && swatchHex === filterColor) return true;
    }

    for (const filterImage of filterImages) {
      if (swatchImageKey && swatchImageKey === filterImage) return true;
    }

    return false;
  }

  function findMatchingSwatch(card, filterColors, filterImages) {
    const swatches = [...card.querySelectorAll('[data-card-swatch]')];
    const matchedSwatch = swatches.find((swatch) => swatch.dataset.swatchMatched === 'true');
    if (matchedSwatch) return matchedSwatch;

    for (const swatch of swatches) {
      if (swatchMatchesFilters(swatch, filterColors, filterImages)) return swatch;
    }
    return null;
  }

  function getCollectionGridCards() {
    const grid = document.querySelector('#collection-product-grid');
    if (!grid) return [];
    return grid.querySelectorAll('collection-card');
  }

  function syncCollectionCardsToFilters() {
    const form = document.querySelector('[data-filters-form]');
    const hasColorFilter = form?.querySelector('[data-filter-input][data-filter-color]:checked');
    const hasColorFilterParam = urlHasColorFilterParams();
    const { colors: filterColors, images: filterImages } = getActiveFilterSwatches();

    getCollectionGridCards().forEach((card) => {
      if (!(card instanceof CollectionCard)) return;

      if (!hasColorFilter && !hasColorFilterParam) {
        card.resetToServerDefault();
        return;
      }

      const matchedSwatch = card.querySelector('[data-card-swatch][data-swatch-matched="true"]');
      if (matchedSwatch) {
        card.applyFilterSwatch(matchedSwatch);
        return;
      }

      const swatch = findMatchingSwatch(card, filterColors, filterImages);
      if (swatch) card.applyFilterSwatch(swatch);
    });
  }

  class CollectionCard extends HTMLElement {
    constructor() {
      super();
      this.onSwatchEnter = this.onSwatchEnter.bind(this);
      this.onSwatchesLeave = this.onSwatchesLeave.bind(this);
    }

    connectedCallback() {
      this.soldOutText = this.dataset.textSoldOut || 'Sold Out';
      this.canHover = window.matchMedia?.('(hover: hover) and (pointer: fine)')?.matches ?? false;
      this.mediaEl = this.querySelector('[data-card-media]');
      this.mainImg = this.mediaEl?.querySelector('img:not([data-card-hover-img])');
      this.hoverImg = this.querySelector('[data-card-hover-img]');
      this.cardLink = this.querySelector('.js-product-card__link');
      this.swatchList = this.querySelector('[data-card-swatches]');
      this.swatches = this.querySelectorAll('[data-card-swatch]');
      this.serverDefaultSwatch =
        this.querySelector('[data-card-swatch].is-active') || this.querySelector('[data-card-swatch]');
      this.defaultSwatch = this.serverDefaultSwatch;
      this.titleEl = this.querySelector('[data-card-title]');
      this.priceEl = this.querySelector('[data-card-price]');
      this.compareEl = this.querySelector('[data-card-compare]');

      if (this.titleEl) {
        this.defaultTitle = this.titleEl.textContent;
        this.serverDefaultTitle = this.defaultTitle;
      }
      if (this.priceEl) {
        this.defaultPrice = this.priceEl.textContent;
        this.serverDefaultPrice = this.defaultPrice;
      }
      if (this.compareEl) {
        this.defaultCompare = this.compareEl.textContent;
        this.defaultCompareHidden = this.compareEl.hidden;
        this.serverDefaultCompare = this.defaultCompare;
        this.serverDefaultCompareHidden = this.defaultCompareHidden;
      }
      this.defaultPriceHasSale = this.priceEl?.classList.contains('product-card__price--sale');
      this.serverDefaultPriceHasSale = this.defaultPriceHasSale;
      if (this.cardLink) this.serverDefaultLink = this.cardLink.href;
      if (this.mainImg) this.serverDefaultImageSrc = this.mainImg.getAttribute('src');

      this.saleBadge = this.mediaEl?.querySelector('[data-badge-type="sale"]');
      this.defaultSaleHidden = this.saleBadge?.hidden ?? true;
      this.serverDefaultSaleHidden = this.defaultSaleHidden;

      this.defaultSoldOut = this.serverDefaultSwatch?.dataset.soldOut === 'true';
      this.serverDefaultSoldOut = this.defaultSoldOut;
      this.updateSoldOutBadge(this.serverDefaultSwatch, false);

      if (this.canHover) {
        for (const swatch of this.swatches) {
          swatch.addEventListener('mouseenter', this.onSwatchEnter);
        }
        this.swatchList?.addEventListener('mouseleave', this.onSwatchesLeave);
      }
    }

    disconnectedCallback() {
      if (this.canHover) {
        for (const swatch of this.swatches || []) {
          swatch.removeEventListener('mouseenter', this.onSwatchEnter);
        }
        this.swatchList?.removeEventListener('mouseleave', this.onSwatchesLeave);
      }
    }

    updateSoldOutBadge(swatch, permanent) {
      const isSoldOut = swatch?.dataset.soldOut === 'true';

      let badgesEl = this.mediaEl?.querySelector('.product-card__badges');

      if (isSoldOut) {
        if (!badgesEl) {
          badgesEl = document.createElement('div');
          badgesEl.className = 'product-card__badges';
          this.mediaEl?.prepend(badgesEl);
        }
        const badge = badgesEl.querySelector('[data-badge-type="sold_out"]');
        if (badge) badge.hidden = false;
      } else {
        const badge = this.mediaEl?.querySelector('[data-badge-type="sold_out"]');
        if (badge) badge.hidden = true;
      }

      if (permanent) {
        this.defaultSoldOut = isSoldOut;
      }
    }

    resetToServerDefault() {
      if (!this.serverDefaultSwatch) return;

      this.querySelector('[data-card-swatch].is-active')?.classList.remove('is-active');
      this.serverDefaultSwatch.classList.add('is-active');
      this.defaultSwatch = this.serverDefaultSwatch;

      if (this.cardLink && this.serverDefaultLink) {
        this.cardLink.href = this.serverDefaultLink;
      }

      if (this.mainImg && this.serverDefaultImageSrc) {
        this.mainImg.setAttribute('srcset', '');
        this.mainImg.setAttribute('src', this.serverDefaultImageSrc);
      }
      if (this.hoverImg) this.hoverImg.classList.remove('is-visible');

      if (this.titleEl && this.serverDefaultTitle) {
        this.titleEl.textContent = this.serverDefaultTitle;
        this.defaultTitle = this.serverDefaultTitle;
      }
      if (this.priceEl && this.serverDefaultPrice) {
        this.priceEl.textContent = this.serverDefaultPrice;
        this.defaultPrice = this.serverDefaultPrice;
        this.priceEl.classList.toggle('product-card__price--sale', this.serverDefaultPriceHasSale);
        this.defaultPriceHasSale = this.serverDefaultPriceHasSale;
      }
      if (this.compareEl) {
        this.compareEl.textContent = this.serverDefaultCompare;
        this.compareEl.hidden = this.serverDefaultCompareHidden;
        this.defaultCompare = this.serverDefaultCompare;
        this.defaultCompareHidden = this.serverDefaultCompareHidden;
      }

      if (this.saleBadge) {
        this.saleBadge.hidden = this.serverDefaultSaleHidden;
        this.defaultSaleHidden = this.serverDefaultSaleHidden;
      }

      this.defaultSoldOut = this.serverDefaultSoldOut;
      this.updateSoldOutBadge(this.serverDefaultSwatch, false);
    }

    applyFilterSwatch(swatch) {
      if (swatch.classList.contains('is-active') && swatch === this.defaultSwatch) {
        this.syncCardLink(swatch);
        return;
      }
      this.selectSwatch(swatch, { skipAnimation: true });
    }

    syncCardLink(swatch) {
      if (this.cardLink && swatch.href) this.cardLink.href = swatch.href;
    }

    selectSwatch(swatch, options = {}) {
      const { skipAnimation = false } = options;
      const imageUrl = swatch.dataset.imageUrl;

      this.querySelector('[data-card-swatch].is-active')?.classList.remove('is-active');
      swatch.classList.add('is-active');
      this.defaultSwatch = swatch;
      this.syncCardLink(swatch);

      if (imageUrl && this.hoverImg && this.mainImg) {
        const currentSrc = this.mainImg.currentSrc || this.mainImg.src || '';
        const alreadyShowing = imageUrl && currentSrc.includes(imageUrl.split('?')[0]);

        if (!alreadyShowing) {
          if (skipAnimation) {
            this.mainImg.setAttribute('srcset', '');
            this.mainImg.setAttribute('src', imageUrl);
            this.hoverImg.classList.remove('is-visible');
          } else {
            this.hoverImg.src = imageUrl;
            this.hoverImg.classList.add('is-visible');

            const swap = () => {
              this.mainImg.setAttribute('srcset', '');
              this.mainImg.setAttribute('src', imageUrl);
              this.hoverImg.classList.remove('is-visible');
            };
            this.hoverImg.addEventListener('transitionend', swap, { once: true });
            setTimeout(swap, 400);
          }
        }
      }

      this.updateInfo(swatch, true);
      this.updateSoldOutBadge(swatch, true);
    }

    onSwatchEnter(event) {
      const swatch = event.currentTarget;
      const imageUrl = swatch.dataset.imageUrl;

      this.querySelector('[data-card-swatch].is-active')?.classList.remove('is-active');
      swatch.classList.add('is-active');

      if (imageUrl && this.hoverImg) {
        this.hoverImg.src = imageUrl;
        this.hoverImg.classList.add('is-visible');
      }

      this.updateInfo(swatch, false);
      this.updateSoldOutBadge(swatch, false);
    }

    onSwatchesLeave() {
      if (this.hoverImg) this.hoverImg.classList.remove('is-visible');

      this.querySelector('[data-card-swatch].is-active')?.classList.remove('is-active');
      this.defaultSwatch?.classList.add('is-active');

      if (this.titleEl) this.titleEl.textContent = this.defaultTitle;
      if (this.priceEl) {
        this.priceEl.textContent = this.defaultPrice;
        this.priceEl.classList.toggle('product-card__price--sale', this.defaultPriceHasSale);
      }
      if (this.compareEl) {
        this.compareEl.textContent = this.defaultCompare;
        this.compareEl.hidden = this.defaultCompareHidden;
      }
      if (this.saleBadge) {
        this.saleBadge.hidden = this.defaultSaleHidden;
      }

      this.updateSoldOutBadge(this.defaultSwatch, false);
    }

    updateInfo(swatch, permanent) {
      if (this.titleEl && swatch.dataset.variantTitle) {
        this.titleEl.textContent = swatch.dataset.variantTitle;
        if (permanent) this.defaultTitle = swatch.dataset.variantTitle;
      }

      if (this.priceEl && swatch.dataset.variantPrice) {
        this.priceEl.textContent = swatch.dataset.variantPrice;
        const hasCompare = swatch.dataset.variantCompare !== '';
        this.priceEl.classList.toggle('product-card__price--sale', hasCompare);

        if (permanent) {
          this.defaultPrice = swatch.dataset.variantPrice;
          this.defaultPriceHasSale = hasCompare;
        }

        if (this.compareEl) {
          this.compareEl.hidden = !hasCompare;
          if (hasCompare) this.compareEl.textContent = swatch.dataset.variantCompare;

          if (permanent) {
            this.defaultCompare = hasCompare ? swatch.dataset.variantCompare : '';
            this.defaultCompareHidden = !hasCompare;
          }
        }

        if (this.saleBadge) {
          this.saleBadge.hidden = !hasCompare;
          if (permanent) this.defaultSaleHidden = !hasCompare;
        }
      }
    }
  }

  customElements.define('collection-card', CollectionCard);

  document.addEventListener('grid:updated', syncCollectionCardsToFilters);
})();
